import type { CSSProperties, KeyboardEvent, ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { MarketingLink, OptionalLink } from "@/components/OptionalLink";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, ExternalLink, Star } from "lucide-react";
import { cn } from "@/lib/utils";

type Testimonial = {
  id: string;
  name: string;
  role?: string;
  comment: string;
  link?: string;
  image?: string;
  rating: number;
  featured?: boolean;
};

const LANDING_TESTIMONIALS_LIMIT = 3;

const testimonialsData: Testimonial[] = [
  {
    id: "producthunt",
    name: "Product Hunt",
    role: "Community review",
    link: "https://www.producthunt.com/products/dollardeploy/reviews",
    comment:
      "Cool and fast way to deploy your app on any server. Their service is quite solid already.",
    rating: 5,
    featured: true
  },
  {
    id: "ryan",
    name: "Ryan",
    role: "New customer",
    comment: "I am a new customer and the experience was great so far.",
    rating: 5
  },
  {
    id: "dhruv",
    name: "Dhruv",
    role: "Developer",
    comment:
      "I really like the simplicity, UI and UX. Please add more tools with a single click deploy.",
    rating: 5
  },
  {
    id: "wesley",
    name: "Wesley",
    role: "Pro customer",
    comment:
      "Good deal, I went ahead and bumped up to the pro plan as I think I have more apps to deploy. Thanks for the quick reply, fantastic service btw.",
    rating: 5
  },
  {
    id: "yannick",
    name: "Yannick",
    role: "Google Reviews",
    link: "https://www.google.com/search?q=dollardeploy&oq=dollardeploy&gs_lcrp=EgZjaHJvbWUyBggAEEUYOTIHCAEQIRigAdIBCTI3NjBqMGo3qAIAsAIA&sourceid=chrome&ie=UTF-8",
    comment:
      "Was able to deploy my first app with their services, really easy to use and understand. Thanks for making this possible!",
    rating: 5
  },
  {
    id: "milan",
    name: "Milan",
    role: "Developer",
    comment:
      "I am impressed by the speed of deploy process. It is way faster than it was on the installed Dokploy.",
    rating: 5
  }
];

const getOrderedTestimonials = (limit?: number) => {
  const featured = testimonialsData.find(t => t.featured);
  const rest = testimonialsData.filter(t => !t.featured);
  const ordered = featured ? [featured, ...rest] : rest;
  return limit ? ordered.slice(0, limit) : ordered;
};

const getInitials = (name: string) =>
  name
    .split(" ")
    .map(part => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const StarRating = ({ rating }: { rating: number }) => (
  <div
    className="flex items-center gap-0.5"
    role="img"
    aria-label={`${rating} out of 5 stars`}
  >
    {Array.from({ length: 5 }).map((_, index) => (
      <Star
        key={index}
        className={cn(
          "size-3.5",
          index < rating ? "fill-primary text-primary" : "fill-muted text-muted"
        )}
        aria-hidden="true"
      />
    ))}
  </div>
);

const TestimonialCard = ({ testimonial }: { testimonial: Testimonial }) => {
  const { name, role, comment, link, image, rating } = testimonial;

  const card = (
    <article className="relative flex h-full min-h-[280px] flex-col overflow-hidden rounded-xl border bg-card p-6 text-left">
      <div className="flex flex-1 flex-col gap-5">
        <StarRating rating={rating} />

        <blockquote className="text-base leading-relaxed text-balance text-foreground">
          &ldquo;{comment}&rdquo;
        </blockquote>

        <footer className="mt-auto flex items-center gap-3 border-t pt-5">
          <Avatar className="size-9 border">
            {image && <AvatarImage alt="" src={image} />}
            <AvatarFallback className="bg-secondary text-xs font-medium text-secondary-foreground">
              {getInitials(name)}
            </AvatarFallback>
          </Avatar>

          <div className="min-w-0 flex-1">
            <p className="truncate font-medium text-foreground">{name}</p>
            {role && <p className="truncate text-sm text-muted-foreground">{role}</p>}
          </div>

          {link && (
            <ExternalLink
              className="size-4 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
          )}
        </footer>
      </div>
    </article>
  );

  if (link) {
    return (
      <OptionalLink
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="block h-full rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        aria-label={`Read ${name} review`}
      >
        {card}
      </OptionalLink>
    );
  }

  return card;
};

const PageEyebrow = ({ children }: { children: ReactNode }) => (
  <div className="mb-3 inline-block rounded-full bg-primary/8 px-3 py-1 font-mono text-xs font-medium lowercase tracking-wide text-primary">
    {children}
  </div>
);

const TestimonialsStats = () => {
  const stats = [
    { value: "5.0", label: "on Product Hunt" },
    { value: `${testimonialsData.length}+`, label: "early reviews" },
    { value: "0", label: "DevOps headaches" }
  ];

  return (
    <section className="scroll-mt-24">
      <div className="mb-8 max-w-[640px]">
        <PageEyebrow>by the numbers</PageEyebrow>
        <h2 className="mb-2.5 font-serif text-[clamp(26px,3.5vw,34px)] font-normal leading-tight tracking-tight text-foreground">
          The real numbers
        </h2>
        <p className="text-[15px] leading-relaxed text-pretty text-muted-foreground">
          Honest numbers from our users — no inflated metrics.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {stats.map(({ value, label }) => (
          <div key={label} className="rounded-xl border bg-card px-6 py-5 text-center">
            <p className="font-serif text-3xl tracking-tight text-foreground">{value}</p>
            <p className="mt-1 text-[13px] font-medium text-secondary-foreground">{label}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

const TestimonialsHero = () => (
  <header className="mx-auto max-w-[760px] pt-20 pb-16 text-center">
    <PageEyebrow>testimonials</PageEyebrow>

    <h1 className="mb-4 font-serif text-[clamp(38px,6vw,60px)] font-normal leading-[1.05] tracking-tight text-balance text-foreground">
      Real feedback from developers who <em className="text-primary">ship with us</em>.
    </h1>

    <p className="text-[17px] leading-relaxed text-pretty text-muted-foreground">
      No cherry-picked case studies — just honest words from people who tried DollarDeploy
      during early access.
    </p>
  </header>
);

const MARQUEE_SECONDS_PER_CARD = 10;

const TestimonialsMarquee = ({ items }: { items: Testimonial[] }) => {
  if (!items.length) {
    return null;
  }

  const marqueeItems = items.length > 1 ? [...items, ...items] : items;
  const marqueeDuration = `${Math.max(items.length, 1) * MARQUEE_SECONDS_PER_CARD}s`;

  return (
    <div
      className="group relative overflow-hidden"
      role="region"
      aria-roledescription="marquee"
      aria-label="Customer reviews"
    >
      <div
        className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-background to-transparent sm:w-20"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-background to-transparent sm:w-20"
        aria-hidden="true"
      />

      <div
        className={cn(
          "flex w-max gap-4 py-1",
          items.length > 1 &&
            "motion-safe:animate-testimonials-marquee motion-reduce:animate-none group-hover:[animation-play-state:paused] group-focus-within:[animation-play-state:paused]"
        )}
        style={
          items.length > 1
            ? ({ "--marquee-duration": marqueeDuration } as CSSProperties)
            : undefined
        }
      >
        {marqueeItems.map((testimonial, index) => (
          <div key={`${testimonial.id}-${index}`} className="w-[360px] shrink-0">
            <TestimonialCard testimonial={testimonial} />
          </div>
        ))}
      </div>
    </div>
  );
};

const TestimonialsMobileSnap = ({ items }: { items: Testimonial[] }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const updateActiveIndex = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    const children = Array.from(el.children) as HTMLElement[];
    if (!children.length) {
      return;
    }

    const center = el.scrollLeft + el.clientWidth / 2;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    children.forEach((child, index) => {
      const childCenter = child.offsetLeft + child.offsetWidth / 2;
      const distance = Math.abs(center - childCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    setActiveIndex(closestIndex);
  }, []);

  useEffect(() => {
    updateActiveIndex();
  }, [items.length, updateActiveIndex]);

  const handleScrollToIndex = (index: number) => {
    const slide = scrollRef.current?.children[index] as HTMLElement | undefined;
    if (!slide) {
      return;
    }
    slide.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  };

  const handlePrevious = () => {
    if (activeIndex > 0) {
      handleScrollToIndex(activeIndex - 1);
    }
  };

  const handleNext = () => {
    if (activeIndex < items.length - 1) {
      handleScrollToIndex(activeIndex + 1);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowLeft" && activeIndex > 0) {
      event.preventDefault();
      handlePrevious();
    }
    if (event.key === "ArrowRight" && activeIndex < items.length - 1) {
      event.preventDefault();
      handleNext();
    }
  };

  return (
    <div>
      <div
        ref={scrollRef}
        tabIndex={0}
        role="region"
        aria-roledescription="carousel"
        aria-label="Customer reviews"
        onScroll={updateActiveIndex}
        onKeyDown={handleKeyDown}
        className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-1 pb-2 [scrollbar-width:none] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [&::-webkit-scrollbar]:hidden"
      >
        {items.map(testimonial => (
          <div key={testimonial.id} className="w-[min(100%,340px)] shrink-0 snap-center">
            <TestimonialCard testimonial={testimonial} />
          </div>
        ))}
      </div>

      {items.length > 1 && (
        <div className="mt-5 flex items-center justify-center gap-3">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-9 shrink-0 rounded-full"
            onClick={handlePrevious}
            disabled={activeIndex === 0}
            aria-label="Previous review"
          >
            <ChevronLeft aria-hidden="true" />
          </Button>

          <div className="flex items-center gap-2">
            {items.map((testimonial, index) => (
              <button
                key={testimonial.id}
                type="button"
                onClick={() => handleScrollToIndex(index)}
                className={cn(
                  "size-2 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  index === activeIndex ? "bg-primary" : "bg-border"
                )}
                aria-label={`Go to review ${index + 1} of ${items.length}`}
                aria-current={index === activeIndex ? "true" : undefined}
              />
            ))}
          </div>

          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-9 shrink-0 rounded-full"
            onClick={handleNext}
            disabled={activeIndex === items.length - 1}
            aria-label="Next review"
          >
            <ChevronRight aria-hidden="true" />
          </Button>
        </div>
      )}
    </div>
  );
};

const TestimonialsReviews = ({ items }: { items: Testimonial[] }) => {
  if (!items.length) {
    return null;
  }

  return (
    <>
      <div className="sm:hidden">
        <TestimonialsMobileSnap items={items} />
      </div>
      <div className="hidden sm:block">
        <TestimonialsMarquee items={items} />
      </div>
    </>
  );
};

const TestimonialsMarqueeSection = ({ landing }: { landing: boolean }) => {
  const items = getOrderedTestimonials(landing ? LANDING_TESTIMONIALS_LIMIT : undefined);
  const reviews = <TestimonialsReviews items={items} />;

  if (landing) {
    return (
      <div className="text-left">
        {reviews}
        {testimonialsData.length > LANDING_TESTIMONIALS_LIMIT && (
          <p className="mt-6 text-center text-sm text-muted-foreground">
            <MarketingLink
              href="/product/testimonials"
              className="border-b border-dashed border-primary/40 text-primary transition-colors hover:text-primary/80"
            >
              View all reviews →
            </MarketingLink>
          </p>
        )}
      </div>
    );
  }

  return (
    <section id="reviews" className="scroll-mt-24">
      <div className="mb-8 max-w-[640px]">
        <PageEyebrow>our users</PageEyebrow>
        <h2 className="mb-2.5 font-serif text-[clamp(26px,3.5vw,34px)] font-normal leading-tight tracking-tight text-foreground">
          Reviews
        </h2>
        <p className="text-[15px] leading-relaxed text-pretty text-muted-foreground">
          Unfiltered feedback from developers and founders using DollarDeploy.
        </p>
      </div>
      {reviews}
    </section>
  );
};

const TestimonialsCta = () => (
  <div className="my-8 flex flex-col items-start justify-between gap-5 rounded-2xl border border-dashed px-6 py-8 sm:flex-row sm:items-center sm:px-8">
    <div className="max-w-[560px]">
      <h2 className="mb-1.5 font-serif text-3xl font-normal tracking-tight text-foreground">
        Ready to see for yourself?
      </h2>
      <p className="text-[15px] leading-relaxed text-pretty text-muted-foreground">
        Join developers who deploy to their own servers without touching SSH or Docker.
      </p>
    </div>
    <div className="flex shrink-0 flex-col gap-2.5 sm:flex-row">
      <MarketingLink
        href="/dashboard"
        className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        Get started free
      </MarketingLink>
      <MarketingLink
        href="https://www.producthunt.com/products/dollardeploy/reviews"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center rounded-full border px-5 py-2.5 text-sm font-medium transition-colors hover:border-foreground hover:bg-foreground hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        Read on Product Hunt
      </MarketingLink>
    </div>
  </div>
);

export const Testimonials = ({ landing = false }: { landing?: boolean }) => {
  if (landing) {
    return (
      <section id="testimonials" className="container text-center">
        <h2 className="mb-4 font-serif text-[clamp(36px,5.5vw,56px)] font-normal leading-[1.05] tracking-tight text-balance text-foreground">
          Some feedback from our
          <em className="text-primary"> early users.</em>
        </h2>

        <div className="mt-10">
          <TestimonialsMarqueeSection landing />
        </div>
      </section>
    );
  }

  return (
    <section id="testimonials" className="bg-background font-sans text-foreground antialiased">
      <div className="mx-auto max-w-[1080px] px-6 sm:px-8">
        <TestimonialsHero />

        <div className="flex flex-col gap-20 pb-12">
          <TestimonialsStats />
          <TestimonialsMarqueeSection landing={false} />
        </div>

        <TestimonialsCta />
      </div>
    </section>
  );
};
